import { cartAvailabilityEnabled } from './cart-availability';

const ADAPTER_KEY = '__raffiCompleteCart20261022';
const READ_MS = 6000;
const READ_TOTAL_MS = 12000;
/** Deliberately excludes buyer identity, addresses, customer records and checkout/order mutations. */
export const COMPLETE_CART_QUERY = `query RaffiCompleteCart($id: ID!, $after: String, $country: CountryCode, $language: LanguageCode)
@inContext(country: $country, language: $language) {
  cart(id: $id) {
    id checkoutUrl createdAt updatedAt note totalQuantity
    attributes { key value }
    cost { totalAmount { amount currencyCode } subtotalAmount { amount currencyCode } totalTaxAmount { amount currencyCode } }
    lines(first: 250, after: $after) {
      pageInfo { hasNextPage endCursor }
      edges { cursor node {
        id quantity attributes { key value }
        merchandise { __typename ... on ProductVariant {
          id title sku
          price { amount currencyCode } compareAtPrice { amount currencyCode }
          image { id url altText width height }
          selectedOptions { name value }
          publicSku: metafield(namespace: "custom", key: "public_sku") { value }
          product { id title publicSku: metafield(namespace: "custom", key: "public_sku") { value } }
        } }
      } }
    }
  }
}`;
const fail = () => new Error('Shopping bag could not be confirmed.');
function within<T>(operation: () => Promise<T>, milliseconds: number): Promise<T> {
  if (milliseconds <= 0) return Promise.reject(fail());
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(fail()), milliseconds);
    Promise.resolve().then(operation).then(value => { clearTimeout(timer); resolve(value); }, () => { clearTimeout(timer); reject(fail()); });
  });
}
const attributes = (value: any) => Array.isArray(value) ? value.map(attribute => ({ key: String(attribute.key), value: String(attribute.value) })) : [];
const metafieldValue = (value: any) => value && typeof value === 'object' ? value.value ?? null : value ?? null;
function lineFromRaw(line: any) {
  const merchandise = line?.merchandise;
  let variant: any = null;
  if (merchandise && (!merchandise.__typename || merchandise.__typename === 'ProductVariant')) {
    const product = merchandise.product ? { ...merchandise.product, publicSku: metafieldValue(merchandise.product.publicSku) } : null;
    variant = { ...merchandise, publicSku: metafieldValue(merchandise.publicSku), image: merchandise.image ? { ...merchandise.image, src: merchandise.image.url } : null, product };
  }
  return { id: line?.id, title: variant?.product?.title || '', quantity: line?.quantity, customAttributes: attributes(line?.attributes), variant, publicSku: variant?.product?.publicSku ?? null };
}
/** Also replaces the SDK's partial mutation formatter so a remaining orphan cannot make it throw. */
function normalizeRawCart(raw: any, locale: string, nodes?: any[]) {
  if (!raw || typeof raw.id !== 'string' || !raw.id || typeof raw.checkoutUrl !== 'string') throw fail();
  const checkoutUrl = raw.checkoutUrl;
  const webUrl = checkoutUrl + (checkoutUrl.includes('?') ? '&' : '?') + 'locale=' + encodeURIComponent(locale);
  return { id: raw.id, createdAt: raw.createdAt, updatedAt: raw.updatedAt, note: raw.note, attributes: attributes(raw.attributes), totalQuantity: raw.totalQuantity,
    webUrl, currencyCode: raw.cost?.totalAmount?.currencyCode, lineItems: (nodes || raw.lines?.edges?.map((edge: any) => edge.node) || []).map(lineFromRaw),
    subtotalPrice: raw.cost?.subtotalAmount, totalPrice: raw.cost?.totalAmount, totalTax: raw.cost?.totalTaxAmount };
}

/** Full, uncached, consistent-generation read using the configured Storefront client. Never retries a mutation. */
export async function fetchCompleteCart(client: any, id: string): Promise<any> {
  if (typeof id !== 'string' || !id) throw fail();
  const expires = Date.now() + READ_TOTAL_MS;
  const request = async (after: string | null) => {
    const variables = { id, after, country: client.country || 'CA', language: client.language || document.documentElement.lang.toUpperCase() || 'EN' };
    const response: any = await within(() => typeof client.client?.request === 'function'
      ? client.client.request(COMPLETE_CART_QUERY, { variables, retries: 0 })
      : typeof client.makeRequest === 'function' ? client.makeRequest(COMPLETE_CART_QUERY, variables) : Promise.reject(fail()), Math.min(READ_MS, expires - Date.now()));
    if (!response || (response.errors && (!Array.isArray(response.errors) || response.errors.length))) throw fail();
    const data = Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
    if (!data || !Object.prototype.hasOwnProperty.call(data, 'cart')) throw fail();
    return data.cart;
  };
  let after: string | null = null, initial: any = null;
  const nodes: any[] = [], cursors = new Set<string>(), ids = new Set<string>();
  while (true) {
    const raw = await request(after);
    if (raw === null && !initial) return null;
    if (!raw || raw.id !== id || typeof raw.updatedAt !== 'string' || !raw.updatedAt || !Number.isSafeInteger(raw.totalQuantity) || raw.totalQuantity < 0 ||
        !Array.isArray(raw.lines?.edges) || typeof raw.lines?.pageInfo?.hasNextPage !== 'boolean') throw fail();
    if (!initial) initial = raw;
    else if (raw.updatedAt !== initial.updatedAt || raw.totalQuantity !== initial.totalQuantity) throw fail();
    for (const edge of raw.lines.edges) {
      const line = edge?.node;
      if (!line || typeof line.id !== 'string' || !line.id || ids.has(line.id) || typeof edge.cursor !== 'string' || !edge.cursor || cursors.has(edge.cursor) || !Number.isSafeInteger(line.quantity) || line.quantity < 1) throw fail();
      ids.add(line.id); cursors.add(edge.cursor); nodes.push(line);
    }
    const page = raw.lines.pageInfo;
    if (!page.hasNextPage) break;
    if (!raw.lines.edges.length || typeof page.endCursor !== 'string' || page.endCursor !== raw.lines.edges[raw.lines.edges.length - 1].cursor || after === page.endCursor || nodes.length >= initial.totalQuantity) throw fail();
    after = page.endCursor;
  }
  if (nodes.reduce((sum, line) => sum + line.quantity, 0) !== initial.totalQuantity) throw fail();
  const locale = `${String(client.language || document.documentElement.lang || 'en').toLowerCase()}-${String(client.country || 'CA').toUpperCase()}`;
  return normalizeRawCart(initial, locale, nodes);
}

/** Install once on approved public surfaces or authenticated staging; protected pages retain the original SDK object. */
export function installCompleteCartAdapter(client: any, options: { onCreatedCart?: (id: string) => void } = {}): any {
  if (!cartAvailabilityEnabled() || client[ADAPTER_KEY]) return client;
  const original = client.cart;
  if (!original || typeof original.fetch !== 'function') throw fail();
  const locale = `${String(client.language || document.documentElement.lang || 'en').toLowerCase()}-${String(client.country || 'CA').toUpperCase()}`;
  client.formatCart = (raw: any) => normalizeRawCart(raw, locale);
  const fetch = (id: string) => fetchCompleteCart(client, id);
  const adapted = { ...original, fetch };
  for (const name of ['create', 'addLineItems', 'removeLineItems', 'updateLineItems', 'updateAttributes']) {
    if (typeof original[name] !== 'function') continue;
    adapted[name] = async (...args: any[]) => {
      let response: any, ambiguous = false;
      try { response = await within(() => original[name](...args), READ_MS); }
      catch { ambiguous = true; }
      const id = name === 'create' ? response?.id : args[0];
      if (typeof id !== 'string' || !id) throw fail();
      if (name === 'create') options.onCreatedCart?.(id);
      // A null mutation response may reflect only the old formatter failing.
      // Confirm its actual outcome with a full read, without repeating the write.
      const confirmed = await fetch(id);
      if (!confirmed) throw fail();
      const rejected = ambiguous || response === null || response === false || response?.userErrors?.length || response?.errors?.length;
      if (name === 'removeLineItems') {
        const removed = Array.isArray(args[1]) ? args[1] : [args[1]];
        if (confirmed.lineItems.some((line: any) => removed.includes(line.id))) throw fail();
      } else if (rejected) throw fail();
      return confirmed;
    };
  }
  Object.defineProperty(client, 'cart', { configurable: true, enumerable: true, writable: true, value: adapted });
  Object.defineProperty(client, ADAPTER_KEY, { value: true });
  return client;
}
